import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Avatar,
  AvatarGroup,
  LinearProgress,
  Breadcrumbs,
  Link,
  Skeleton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  ArrowLeft,
  Plus,
  Settings,
  MoreVertical,
  Clock,
  Users,
  FolderTree,
  ListTodo,
  BarChart3,
  Calendar,
  Edit2,
  Trash2,
  ChevronRight,
  GanttChart,
  Target,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CheckCircle } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { openModal } from '@/store/slices/uiSlice';
import { Project, ProjectStatus, Task, TaskStatus } from '@/types';
import { projectService, taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import StatusBadge from '@/shared/components/StatusBadge';
import { isAdminOrManager } from '@/shared/utils/roles';
import ProjectTasksTab from '../components/ProjectTasksTab';
import ProjectTreeView from '../components/ProjectTreeView';
import ProjectOverviewTab from '../components/ProjectOverviewTab';
import ProjectTeamTab from '../components/ProjectTeamTab';
import MilestonesManagement from '../components/MilestonesManagement';
import ProjectEditModal from '../components/ProjectEditModal';
import ProjectSettingsModal from '../components/ProjectSettingsModal';
import DeleteProjectDialog from '../components/DeleteProjectDialog';
import ProjectTasksGanttChart from '../components/ProjectTasksGanttChart';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Project status colors
const statusColors: Record<ProjectStatus, string> = {
  [ProjectStatus.PLANNING]: '#64748b',
  [ProjectStatus.ACTIVE]: '#2563eb',
  [ProjectStatus.ON_HOLD]: '#f59e0b',
  [ProjectStatus.COMPLETED]: '#16a34a',
  [ProjectStatus.CANCELLED]: '#ef4444',
};

/**
 * ProjectDetailPage
 * Comprehensive project view with tasks, hierarchy, team
 * Master project management interface
 */
const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector(selectCurrentUser);
  const canManageProjects = isAdminOrManager(user?.role);
  
  const [activeTab, setActiveTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [closeProjectConfirmOpen, setCloseProjectConfirmOpen] = useState(false);

  // Fetch project details
  const { data: projectData, isLoading: projectLoading } = useQuery({
    queryKey: QUERY_KEYS.project(projectId!),
    queryFn: () => projectService.getProject(projectId!),
    enabled: !!projectId,
  });

  const project = projectData ?? undefined;

  // Fetch project tasks
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: QUERY_KEYS.projectTasks(projectId!),
    queryFn: () => taskService.getTasksByProject(projectId!),
    enabled: !!projectId,
  });

  const tasks = tasksData ?? [];

  // Fetch child projects
  const { data: childProjectsData } = useQuery({
    queryKey: ['projects', projectId, 'children'],
    queryFn: () => projectService.getProjectChildren(projectId!),
    enabled: !!projectId,
  });

  const childProjects = childProjectsData ?? [];

  const closeProjectMutation = useMutation({
    mutationFn: () =>
      projectService.updateProject(projectId!, { status: ProjectStatus.COMPLETED }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.project(projectId!) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projects });
      setCloseProjectConfirmOpen(false);
    },
  });

  const handleCloseProjectConfirm = () => {
    closeProjectMutation.mutate();
  };

  const calculateStats = () => {
    if (!tasks.length) {
      return {
        totalTasks: 0,
        completedTasks: 0,
        progress: 0,
        totalTime: 0,
        estimatedTime: 0,
      };
    }

    const completedTasks = tasks.filter(
      (t: Task) => t.status === TaskStatus.COMPLETED
    ).length;
    const progressFromTasks = Math.round((completedTasks / tasks.length) * 100);
    const progress =
      project?.status === ProjectStatus.COMPLETED ? 100 : progressFromTasks;
    const totalTime = tasks.reduce(
      (sum: number, t: Task) => sum + (t.actualHours || 0) * 60,
      0
    );
    const estimatedTime = tasks.reduce(
      (sum: number, t: Task) => sum + (t.estimatedHours || 0) * 60,
      0
    );

    return {
      totalTasks: tasks.length,
      completedTasks,
      progress,
      totalTime,
      estimatedTime,
    };
  };

  const stats = calculateStats();

  const handleCreateTask = () => {
    dispatch(openModal({ type: 'taskCreate', data: { projectId } }));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditProject = () => {
    handleMenuClose();
    setEditModalOpen(true);
  };

  const handleOpenSettings = () => {
    handleMenuClose();
    setSettingsModalOpen(true);
  };

  const handleDeleteProject = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (projectLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="text" width="40%" />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mt: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={100} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={400} sx={{ mt: 3 }} />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Project not found
        </Typography>
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/projects')}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          Dashboard
        </Link>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/projects')}
        >
          Projects
        </Link>
        {project.parentId && (
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/projects/${project.parentId}`)}
          >
            Parent Project
          </Link>
        )}
        <Typography color="text.primary">{project.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h5" fontWeight={600}>
                {project.name}
              </Typography>
              <Chip
                label={project.status.replace('_', ' ')}
                size="small"
                sx={{
                  bgcolor: `${statusColors[project.status]}20`,
                  color: statusColors[project.status],
                  fontWeight: 500,
                  textTransform: 'capitalize',
                }}
              />
              {canManageProjects && project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.CANCELLED && (
                <Chip
                  icon={<CheckCircle size={14} />}
                  label="Close Project"
                  size="small"
                  onClick={() => setCloseProjectConfirmOpen(true)}
                  sx={{
                    bgcolor: 'success.50',
                    color: 'success.main',
                    fontWeight: 500,
                    '&:hover': { bgcolor: 'success.100' },
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {project.description || 'No description'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={handleCreateTask}
          >
            Add Task
          </Button>
          {canManageProjects && (
            <>
              <IconButton onClick={handleMenuOpen}>
                <MoreVertical size={20} />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={handleEditProject}>
                  <Edit2 size={16} style={{ marginRight: 8 }} />
                  Edit Project
                </MenuItem>
                <MenuItem onClick={handleOpenSettings}>
                  <Settings size={16} style={{ marginRight: 8 }} />
                  Settings
                </MenuItem>
                {project.status !== ProjectStatus.COMPLETED && project.status !== ProjectStatus.CANCELLED && (
                  <MenuItem onClick={() => { handleMenuClose(); setCloseProjectConfirmOpen(true); }}>
                    <CheckCircle size={16} style={{ marginRight: 8 }} />
                    Close Project
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleDeleteProject} sx={{ color: 'error.main' }}>
                  <Trash2 size={16} style={{ marginRight: 8 }} />
                  Delete Project
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 2,
          mb: 3,
        }}
      >
        {/* Progress Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BarChart3 size={18} style={{ color: '#2563eb' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Progress
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {stats.progress}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={stats.progress}
              sx={{
                mt: 1,
                height: 6,
                borderRadius: 1,
                bgcolor: 'grey.100',
              }}
            />
          </CardContent>
        </Card>

        {/* Tasks Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ListTodo size={18} style={{ color: '#16a34a' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Tasks
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {stats.completedTasks}/{stats.totalTasks}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              completed
            </Typography>
          </CardContent>
        </Card>

        {/* Time Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Clock size={18} style={{ color: '#f59e0b' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Time Tracked
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={600}>
              {formatDuration(stats.totalTime)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              of {formatDuration(stats.estimatedTime)} estimated
            </Typography>
          </CardContent>
        </Card>

        {/* Deadline Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Calendar size={18} style={{ color: '#ef4444' }} />
              <Typography variant="subtitle2" color="text.secondary">
                Deadline
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={600}>
              {dayjs(project.endDate).format('MMM D, YYYY')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: dayjs(project.endDate).isBefore(dayjs())
                  ? 'error.main'
                  : 'text.secondary',
              }}
            >
              {dayjs(project.endDate).fromNow()}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
        >
          <Tab
            label="Overview"
            icon={<BarChart3 size={16} />}
            iconPosition="start"
          />
          <Tab label="Tasks" icon={<ListTodo size={16} />} iconPosition="start" />
          <Tab
            label="Milestones"
            icon={<Target size={16} />}
            iconPosition="start"
          />
          <Tab
            label="Hierarchy"
            icon={<FolderTree size={16} />}
            iconPosition="start"
          />
          <Tab
            label="Timeline"
            icon={<GanttChart size={16} />}
            iconPosition="start"
          />
          <Tab label="Team" icon={<Users size={16} />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {/* Overview Tab */}
          <TabPanel value={activeTab} index={0}>
            <ProjectOverviewTab
              project={project}
              tasks={tasks}
              stats={stats}
            />
          </TabPanel>

          {/* Tasks Tab */}
          <TabPanel value={activeTab} index={1}>
            <ProjectTasksTab
              projectId={projectId!}
              tasks={tasks}
              isLoading={tasksLoading}
              onCreateTask={handleCreateTask}
            />
          </TabPanel>

          {/* Milestones Tab */}
          <TabPanel value={activeTab} index={2}>
            <MilestonesManagement
              project={project}
              tasks={tasks}
            />
          </TabPanel>

          {/* Hierarchy Tab */}
          <TabPanel value={activeTab} index={3}>
            <ProjectTreeView
              project={project}
              childProjects={childProjects}
              tasks={tasks}
            />
          </TabPanel>

          {/* Timeline Tab */}
          <TabPanel value={activeTab} index={4}>
            <ProjectTasksGanttChart
              project={project}
              tasks={tasks}
              milestones={project.milestones || []}
              isLoading={tasksLoading}
            />
          </TabPanel>

          {/* Team Tab */}
          <TabPanel value={activeTab} index={5}>
            <ProjectTeamTab project={project} tasks={tasks} />
          </TabPanel>
        </Box>
      </Card>

      {/* Project Edit Modal */}
      <ProjectEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        project={project}
      />

      {/* Project Settings Modal */}
      <ProjectSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        project={project}
        onDelete={() => setDeleteDialogOpen(true)}
      />

      {/* Delete Project Dialog */}
      <DeleteProjectDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        project={project}
      />

      {/* Close Project Confirmation */}
      <ConfirmationModal
        open={closeProjectConfirmOpen}
        onClose={() => setCloseProjectConfirmOpen(false)}
        data={{
          title: 'Close project',
          message: `Mark "${project?.name}" as completed? This will close the project.`,
          confirmText: 'Close project',
          cancelText: 'Cancel',
          type: 'success',
          onConfirm: handleCloseProjectConfirm,
        }}
      />
    </Box>
  );
};

export default ProjectDetailPage;
